import { Typography } from 'design_system/src';
import { getActiveCreditPolicy } from 'models/loan-offer/controllers/loan-offer.controller';
import {
	formatMopRange,
	formatOutcomeRange,
	formatOverdueBalance,
} from 'models/loan-offer/helpers/credit-policy.helpers';
import { CreditPolicy } from 'models/loan-offer/interfaces/credit-policy.interface';
import { priceFormatFinancial } from 'models/shared/helpers/prices';
import { useEffect, useState } from 'react';

function CreditPolicyMatrix() {
	const [policy, setPolicy] = useState<CreditPolicy | null>(null);

	useEffect(() => {
		getActiveCreditPolicy()
			.then(setPolicy)
			.catch(() => {});
	}, []);

	if (!policy) return null;

	const approvedRows = policy.eligibilityRows.filter(
		(r) => r.approved && r.mop !== undefined,
	);
	const rejectedRows = policy.eligibilityRows.filter((r) => !r.approved);
	const noBureau = policy.eligibilityRows.find(
		(r) => r.approved && r.mop === undefined,
	);

	const durations = [...new Set(policy.rates.map((r) => r.duration))].sort(
		(a, b) => a - b,
	);
	const profileCodes = [...new Set(policy.rates.map((r) => r.profileCode))];

	const getRate = (profileCode: string, duration: number) => {
		const rate = policy.rates.find(
			(r) => r.profileCode === profileCode && r.duration === duration,
		);
		return rate ? `${(rate.expDefaultRate * 100).toFixed(1)}%` : '--';
	};

	const getProfile = (code: string) =>
		policy.profiles.find((p) => p.code === code);

	return (
		<div className="br_xs shadow_soft bg_neutral_0 p_md border_1 border_solid border_neutral_400">
			<div className="display_flex flex_justify_between flex_align_center m_b_md">
				<Typography scale="large" weight="600" textColor="neutral_900">
					Política de crédito activa
				</Typography>
				<div className="display_flex flex_gap_sm flex_align_center">
					<div
						className="dim_xs dim_xs br_full bg_green_300_nocontrast"
						style={{ width: 10, height: 10, borderRadius: '50%' }}
					/>
					<Typography scale="small" weight="600" textColor="neutral_700">
						{policy.policyVersion}
					</Typography>
				</div>
			</div>

			<div className="display_flex flex_gap_md" style={{ overflowX: 'auto' }}>
				{/* Elegibilidad */}
				<div style={{ minWidth: 260 }}>
					<Typography
						scale="small"
						weight="600"
						textColor="neutral_700"
						className="m_b_xs"
					>
						Elegibilidad
					</Typography>
					<table
						style={{
							width: '100%',
							borderCollapse: 'collapse',
							fontSize: '12px',
						}}
					>
						<thead>
							<tr className="bg_neutral_200">
								<th
									style={{
										padding: '6px 10px',
										textAlign: 'left',
										fontWeight: 600,
									}}
								>
									Perfil
								</th>
								<th
									style={{
										padding: '6px 10px',
										textAlign: 'left',
										fontWeight: 600,
									}}
								>
									Rango
								</th>
								<th
									style={{
										padding: '6px 10px',
										textAlign: 'center',
										fontWeight: 600,
									}}
								>
									Estado
								</th>
							</tr>
						</thead>
						<tbody>
							{approvedRows.map((row) => (
								<tr
									key={row.id}
									className="border_b_1 border_solid border_neutral_200"
								>
									<td
										style={{ padding: '5px 10px', fontWeight: 600 }}
										className="text_green_300"
									>
										{row.profileCode}
									</td>
									<td
										style={{ padding: '5px 10px' }}
										className="text_neutral_700"
									>
										{formatMopRange(row.mop)}
										{formatOverdueBalance(row.overdueBalance)}
										{formatOutcomeRange(row.outcome)}
									</td>
									<td style={{ padding: '5px 10px', textAlign: 'center' }}>
										<span
											className="bg_green_300_nocontrast text_neutral_0 br_xs p_x_xs"
											style={{ fontSize: 11 }}
										>
											Aprobado
										</span>
									</td>
								</tr>
							))}
							{rejectedRows.map((row) => (
								<tr
									key={row.id}
									className="border_b_1 border_solid border_neutral_200"
								>
									<td
										style={{ padding: '5px 10px', fontWeight: 600 }}
										className="text_red_300"
									>
										{row.profileCode}
									</td>
									<td
										style={{ padding: '5px 10px' }}
										className="text_neutral_700"
									>
										{formatMopRange(row.mop)}
									</td>
									<td style={{ padding: '5px 10px', textAlign: 'center' }}>
										<span
											className="bg_red_300_nocontrast text_neutral_0 br_xs p_x_xs"
											style={{ fontSize: 11 }}
										>
											Rechazado
										</span>
									</td>
								</tr>
							))}
							{noBureau && (
								<tr className="border_b_1 border_solid border_neutral_200">
									<td
										style={{ padding: '5px 10px', fontWeight: 600 }}
										className="text_green_300"
									>
										{noBureau.profileCode}
									</td>
									<td
										style={{ padding: '5px 10px' }}
										className="text_neutral_700"
									>
										{formatMopRange()}
									</td>
									<td style={{ padding: '5px 10px', textAlign: 'center' }}>
										<span
											className="bg_green_300_nocontrast text_neutral_0 br_xs p_x_xs"
											style={{ fontSize: 11 }}
										>
											Aprobado
										</span>
									</td>
								</tr>
							)}
						</tbody>
					</table>
					<Typography
						scale="small"
						weight="400"
						textColor="neutral_600"
						className="m_t_xs"
					>
						MOP: Calificación según los días de atraso. MOP mayor a 3 son 60
						días o más de atraso.
					</Typography>
					<Typography
						scale="small"
						weight="400"
						textColor="neutral_600"
						className="m_t_xs"
					>
						CPM: Compromiso de pagos mensual calculado desde Círculo de Crédito
					</Typography>
					<Typography
						scale="small"
						weight="400"
						textColor="neutral_600"
						className="m_t_xs"
					>
						SV: Saldo vencido de sus cuentas activas
					</Typography>
				</div>

				{/* Tasas */}
				<div style={{ minWidth: 340 }}>
					<Typography
						scale="small"
						weight="600"
						textColor="neutral_700"
						className="m_b_xs"
					>
						Tasa de default esperada
					</Typography>
					<table
						style={{
							width: '100%',
							borderCollapse: 'collapse',
							fontSize: '12px',
						}}
					>
						<thead>
							<tr className="bg_neutral_200">
								<th
									style={{
										padding: '6px 10px',
										textAlign: 'left',
										fontWeight: 600,
									}}
								>
									Perfil
								</th>
								{durations.map((d) => (
									<th
										key={d}
										style={{
											padding: '6px 10px',
											textAlign: 'center',
											fontWeight: 600,
										}}
									>
										{d}s
									</th>
								))}
								<th
									style={{
										padding: '6px 10px',
										textAlign: 'center',
										fontWeight: 600,
									}}
								>
									Enganche min.
								</th>
							</tr>
						</thead>
						<tbody>
							{profileCodes.map((code) => {
								const profile = getProfile(code);
								const isApproved =
									policy.eligibilityRows.find((r) => r.profileCode === code)
										?.approved ?? true;
								return (
									<tr
										key={code}
										className="border_b_1 border_solid border_neutral_200"
									>
										<td
											style={{ padding: '5px 10px', fontWeight: 600 }}
											className={isApproved ? 'text_green_300' : 'text_red_300'}
										>
											{code}
										</td>
										{durations.map((d) => (
											<td
												key={d}
												style={{ padding: '5px 10px', textAlign: 'center' }}
												className="text_neutral_800"
											>
												{getRate(code, d)}
											</td>
										))}
										<td
											style={{ padding: '5px 10px', textAlign: 'center' }}
											className="text_neutral_700"
										>
											{profile?.minDownPaymentPct != null
												? `${(profile.minDownPaymentPct * 100).toFixed(0)}%`
												: '--'}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>

				{/* Parámetros */}
				<div style={{ minWidth: 200 }}>
					<Typography
						scale="small"
						weight="600"
						textColor="neutral_700"
						className="m_b_xs"
					>
						Restricciones
					</Typography>
					<table
						style={{
							width: '100%',
							borderCollapse: 'collapse',
							fontSize: '12px',
						}}
					>
						<tbody>
							{[
								{
									label: 'Monto mín.',
									value: priceFormatFinancial(
										policy.capacity.minFinancingAmount ?? 0,
									),
								},
								{
									label: 'Monto máx.',
									value: priceFormatFinancial(
										policy.capacity.maxFinancingAmount ?? 0,
									),
								},
								{
									label: 'Enganche máx.',
									value: `${(
										policy.constraints.maxDownPaymentPct * 100
									).toFixed(0)}%`,
								},
								{
									label: 'Tasa nominal máx.',
									value: `${(policy.constraints.maxNominalRate * 100).toFixed(
										0,
									)}%`,
								},
								{
									label: 'Capacidad pago',
									value: `${(policy.capacity.paymentCapacity * 100).toFixed(
										0,
									)}%`,
								},
								{
									label: 'GPS semanal',
									value: priceFormatFinancial(policy.gps.periodicGpsAmount),
								},
								{
									label: 'GPS compra',
									value: priceFormatFinancial(policy.gps.purchaseGpsAmount),
								},
								{
									label: 'Vigencia pre-oferta',
									value: `${policy.constraints.preOfferExpirationDays} días`,
								},
								{
									label: 'Vigencia oferta',
									value: `${policy.constraints.offerExpirationDays} días`,
								},
							].map(({ label, value }) => (
								<tr
									key={label}
									className="border_b_1 border_solid border_neutral_200"
								>
									<td
										style={{ padding: '5px 10px' }}
										className="text_neutral_600"
									>
										{label}
									</td>
									<td
										style={{ padding: '5px 10px', fontWeight: 600 }}
										className="text_neutral_900"
									>
										{value}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
}

export default CreditPolicyMatrix;
